/**
 * "Publicering" (slice B, P1-12): when each lecture's questions open for students. One course
 * setting (days before the lecture, 00:00 Stockholm) and, per lecture, an override: own date,
 * "Öppna nu" or "Stängd tills vidare". The server enforces it; teachers always see everything.
 */
import { useEffect, useState } from "react";
import { useLocalizedText } from "@client/components/Localized";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Spinner,
  useToast,
} from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { formatDayShort } from "@client/lib/format";
import { DEFAULT_TIMEZONE, dayKey } from "@shared/time";
import { TeacherNav } from "./TeacherNav";
import { useSetLectureRelease, useSetReleaseOffset, useTeacherReleases } from "./api";
import type { LectureReleaseChange, TeacherLectureRelease, TeacherReleases } from "./types";

type Mode = LectureReleaseChange["mode"];

const MODES: Mode[] = ["default", "date", "open", "closed"];

function modeOf(release: TeacherLectureRelease): Mode {
  return release.source;
}

function OffsetForm({ data }: { data: TeacherReleases }) {
  const t = useT();
  const toast = useToast();
  const save = useSetReleaseOffset();
  const [value, setValue] = useState(String(data.offsetDays));
  useEffect(() => setValue(String(data.offsetDays)), [data.offsetDays]);
  const days = Number(value);
  const valid = value.trim() !== "" && Number.isInteger(days) && days >= 0;

  return (
    <Card compact className="mb-4">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!valid) return;
          try {
            await save.mutateAsync(days);
            toast.success(t("teacher.releases.saved"));
          } catch (error) {
            toast.error(messageForError(error, t));
          }
        }}
      >
        <Input
          type="number"
          min={0}
          max={data.maxOffsetDays}
          step={1}
          inputMode="numeric"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          label={t("teacher.releases.offset.label")}
          hint={t("teacher.releases.offset.hint", { days: data.defaultOffsetDays })}
          suffix={t("teacher.releases.offset.suffix")}
          containerClassName="w-full max-w-sm"
        />
        <Button type="submit" variant="secondary" disabled={!valid} loading={save.isPending}>
          {t("teacher.releases.save")}
        </Button>
      </form>
    </Card>
  );
}

function OpensCell({ release }: { release: TeacherLectureRelease }) {
  const t = useT();
  const { lang } = useLang();
  if (release.opensAt !== null)
    return <>{formatDayShort(dayKey(release.opensAt, DEFAULT_TIMEZONE), lang)}</>;
  return <>{t(`teacher.releases.opens.${release.source === "open" ? "open" : "closed"}`)}</>;
}

function ReleaseRow({
  release,
  offsetDays,
}: {
  release: TeacherLectureRelease;
  offsetDays: number;
}) {
  const t = useT();
  const { lang } = useLang();
  const localize = useLocalizedText();
  const toast = useToast();
  const save = useSetLectureRelease();
  const [mode, setMode] = useState<Mode>(modeOf(release));
  const [opensOn, setOpensOn] = useState(release.opensOn ?? release.date);
  useEffect(() => {
    setMode(modeOf(release));
    setOpensOn(release.opensOn ?? release.date);
  }, [release]);
  const change: LectureReleaseChange =
    mode === "date" ? { mode, opensOn } : ({ mode } as LectureReleaseChange);
  const dirty =
    mode !== modeOf(release) || (mode === "date" && opensOn !== (release.opensOn ?? ""));

  return (
    <tr className="border-b border-ink-100 align-top last:border-0 dark:border-ink-900">
      <th scope="row" className="py-3 pr-3 text-left font-medium text-ink-800 dark:text-ink-100">
        {release.id}
        <span className="block text-xs font-normal text-ink-500 dark:text-ink-400">
          {localize(release.title)}
        </span>
      </th>
      <td className="py-3 pr-3 whitespace-nowrap">{formatDayShort(release.date, lang)}</td>
      <td className="py-3 pr-3 whitespace-nowrap">
        <OpensCell release={release} />
      </td>
      <td className="py-3 pr-3">
        <Badge tone={release.open ? "success" : "neutral"}>
          {t(`teacher.releases.state.${release.open ? "open" : "closed"}`)}
        </Badge>
      </td>
      <td className="py-3">
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={async (event) => {
            event.preventDefault();
            try {
              await save.mutateAsync({ lectureId: release.id, change });
              toast.success(t("teacher.releases.saved"));
            } catch (error) {
              toast.error(messageForError(error, t));
            }
          }}
        >
          <Select
            aria-label={t("teacher.releases.modeLabel", { id: release.id })}
            value={mode}
            onChange={(event) => setMode(event.target.value as Mode)}
            options={MODES.map((value) => ({
              value,
              label: t(`teacher.releases.mode.${value}`, { days: offsetDays }),
            }))}
          />
          {mode === "date" && (
            <Input
              type="date"
              aria-label={t("teacher.releases.dateLabel", { id: release.id })}
              value={opensOn}
              required
              onChange={(event) => setOpensOn(event.target.value)}
            />
          )}
          <Button
            type="submit"
            size="sm"
            variant="secondary"
            disabled={!dirty || (mode === "date" && !opensOn)}
            loading={save.isPending}
            aria-label={t("teacher.releases.saveLabel", { id: release.id })}
          >
            {t("teacher.releases.save")}
          </Button>
        </form>
      </td>
    </tr>
  );
}

export function TeacherReleasesPage() {
  const t = useT();
  const query = useTeacherReleases();

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title={t("teacher.releases.title")}
        description={t("teacher.releases.description")}
      />
      <TeacherNav />
      {query.isLoading && <Spinner size="md" label={t("common.loading")} />}
      {query.isError && <EmptyState title={t("teacher.releases.error")} />}
      {query.data && (
        <>
          <OffsetForm data={query.data} />
          <Card compact>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-ink-700 dark:text-ink-200">
                <caption className="sr-only">{t("teacher.releases.title")}</caption>
                <thead>
                  <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500 dark:border-ink-800 dark:text-ink-400">
                    <th scope="col" className="py-2 pr-3 font-medium">
                      {t("teacher.releases.col.lecture")}
                    </th>
                    <th scope="col" className="py-2 pr-3 font-medium">
                      {t("teacher.releases.col.date")}
                    </th>
                    <th scope="col" className="py-2 pr-3 font-medium">
                      {t("teacher.releases.col.opens")}
                    </th>
                    <th scope="col" className="py-2 pr-3 font-medium">
                      {t("teacher.releases.col.state")}
                    </th>
                    <th scope="col" className="py-2 font-medium">
                      {t("teacher.releases.col.change")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.lectures.map((release) => (
                    <ReleaseRow
                      key={release.id}
                      release={release}
                      offsetDays={query.data.offsetDays}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

export default TeacherReleasesPage;
